import React, { useState, useEffect } from "react";
import DashboardLayout from "../../layoutcontainers/DashboardLayout";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Chip,
} from "@mui/material";
import MaterialTypography from "../../components/MaterialTypography";
import MaterialButton from "../../components/MaterialButton";
import BASEURL from "../Utils/Baseurl";
import Swal from "sweetalert2";

const OTConfiguration = () => {
  const [config, setConfig] = useState(null);
  const [selectedMode, setSelectedMode] = useState("ot_absorb_late");
  const [loading, setLoading] = useState(false);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        BASEURL,
        {
          query: `query {
            activeOtConfiguration {
              id
              name
              otMode
              otModeDisplay
              isActive
            }
          }`,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      const data = response.data?.data?.activeOtConfiguration;
      if (data) {
        setConfig(data);
        setSelectedMode(data.otMode.toLowerCase());
      }
    } catch (error) {
      console.error("Error fetching OT config:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      if (config?.id) {
        // Update existing
        await axios.post(
          BASEURL,
          {
            query: `mutation UpdateOTConfig($id: ID!, $otMode: String) {
              updateOtConfiguration(id: $id, otMode: $otMode, isActive: true) {
                otConfiguration {
                  id otMode otModeDisplay isActive
                }
              }
            }`,
            variables: { id: config.id, otMode: selectedMode },
          },
          { headers: { "Content-Type": "application/json" } }
        );
      } else {
        // Create new
        await axios.post(
          BASEURL,
          {
            query: `mutation CreateOTConfig($name: String!, $otMode: String!) {
              createOtConfiguration(name: $name, otMode: $otMode) {
                otConfiguration {
                  id otMode otModeDisplay isActive
                }
              }
            }`,
            variables: { name: "Default OT Config", otMode: selectedMode },
          },
          { headers: { "Content-Type": "application/json" } }
        );
      }
      Swal.fire({
        position: "center",
        icon: "success",
        title: "OT Configuration Updated!",
        showConfirmButton: false,
        timer: 2000,
      });
      fetchConfig();
    } catch (error) {
      console.error("Error saving OT config:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save OT configuration.",
      });
    } finally {
      setLoading(false);
    }
  };

  const modeDescriptions = {
    ot_absorb_late: {
      title: "Config 1: OT Absorbs Late",
      description:
        "Late minutes are deducted from overtime. If OT cannot cover all late minutes, the remainder becomes LOP (Loss of Pay). This is the default behavior for Operators.",
      example:
        "Example: Late 60 mins, OT 200 mins → Paid OT = 140 mins, LOP = 0",
    },
    ot_covers_shift: {
      title: "Config 2: OT Covers Missing Shift Hours",
      description:
        "OT first covers any missing shift hours (from late arrivals and absent days). Only the excess OT beyond the missing hours is paid. If OT cannot cover all missing shift hours, the remainder becomes LOP.",
      example:
        "Example: Missing shift 136 hrs, OT 200 hrs → Paid OT = 64 hrs, LOP = 0",
    },
  };

  return (
    <DashboardLayout>
      <Card>
        <CardHeader
          title={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <MaterialTypography variant="h5" fontWeight="medium">
                OT Configuration
              </MaterialTypography>
              {config && (
                <Chip
                  label={`Active: ${modeDescriptions[selectedMode]?.title || selectedMode}`}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Stack>
          }
        />
        <CardContent sx={{ minHeight: "50vh" }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 2, fontWeight: "bold" }}>
              Select OT Calculation Mode
            </FormLabel>
            <RadioGroup
              value={selectedMode}
              onChange={(e) => setSelectedMode(e.target.value)}
            >
              {Object.entries(modeDescriptions).map(([key, info]) => (
                <Card
                  key={key}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    p: 2,
                    border: selectedMode === key ? "2px solid #3F51B5" : "1px solid #ddd",
                    backgroundColor: selectedMode === key ? "#f5f7ff" : "white",
                    cursor: "pointer",
                  }}
                  onClick={() => setSelectedMode(key)}
                >
                  <FormControlLabel
                    value={key}
                    control={<Radio />}
                    label={
                      <Stack spacing={0.5}>
                        <MaterialTypography variant="subtitle1" fontWeight="bold">
                          {info.title}
                        </MaterialTypography>
                        <MaterialTypography variant="body2" color="text">
                          {info.description}
                        </MaterialTypography>
                        <MaterialTypography
                          variant="caption"
                          sx={{ color: "#666", fontStyle: "italic" }}
                        >
                          {info.example}
                        </MaterialTypography>
                      </Stack>
                    }
                    sx={{ alignItems: "flex-start", m: 0 }}
                  />
                </Card>
              ))}
            </RadioGroup>
          </FormControl>

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <MaterialButton
              variant="gradient"
              color="dark"
              onClick={handleSave}
              disabled={loading || (config && selectedMode === config.otMode.toLowerCase())}
            >
              {loading ? "Saving..." : "Save Configuration"}
            </MaterialButton>
          </Stack>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default OTConfiguration;
